import React, { useEffect, useMemo } from "react";
import {
	useNavigate,
	Routes,
	Route,
	useLocation,
} from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
	FiUser,
	FiShoppingBag,
} from "react-icons/fi";
import { HiTicket } from "react-icons/hi";

import DashboardLayout from "../components/Layout/DashboardLayout";
import MyTickets from "../components/MyPage/MyTickets";
import PurchaseHistory from "../components/MyPage/PurchaseHistory";
import Profile from "../components/MyPage/Profile";
import { usePageTitle } from "../hooks/usePageTitle";

const MyPage: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { isAuthenticated, isAuthLoading, login, logout } = useAuth();
	const currentPath = location.pathname;
	const pageInfo = useMemo(() => {
		const normalizedPath = currentPath
			.replace("/mypage/", "")
			.replace("/mypage", "");

		if (normalizedPath === "") {
			return { title: "マイチケット", subtitle: "購入済みチケットの確認" };
		}
		if (normalizedPath === "history") {
			return { title: "購入履歴", subtitle: "これまでの購入記録" };
		}
		if (normalizedPath === "profile") {
			return { title: "プロフィール設定", subtitle: "アカウント情報の管理" };
		}

		return { title: "マイページ", subtitle: "" };
	}, [currentPath]);

	usePageTitle(pageInfo.title);

	// Auth guard - redirect to login if not authenticated
	useEffect(() => {
		if (!isAuthLoading && !isAuthenticated) {
			login();
		}
	}, [isAuthLoading, isAuthenticated, login]);

	// Make sure we're showing a loading state while checking auth
	if (isAuthLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
					<p className="mt-4 text-gray-600 dark:text-gray-400">ページを読み込み中...</p>
				</div>
			</div>
		);
	}

	// Define the navigation items with react-icons
	const menuItems = [
		{
			path: "",
			label: "マイチケット",
			icon: <HiTicket className="h-5 w-5" />,
			badge: undefined
		},
		{
			path: "history",
			label: "購入履歴",
			icon: <FiShoppingBag className="h-5 w-5" />
		},
		{
			path: "profile",
			label: "プロフィール設定",
			icon: <FiUser className="h-5 w-5" />
		},
	];

	const handleLogout = async () => {
		try {
			await logout();
			navigate("/");
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	return (
		<DashboardLayout
			title={pageInfo.title}
			subtitle={pageInfo.subtitle}
			menuItems={menuItems}
			basePath="/mypage"
			appName="マイページ"
			appDescription="チケット管理"
			logoIcon={<FiUser className="h-6 w-6" />}
			onLogout={handleLogout}
			showHeader={false}
			accentColor="blue"
		>
			<Routes>
				<Route path="/" element={<MyTickets />} />
				<Route path="/history" element={<PurchaseHistory />} />
				<Route path="/profile" element={<Profile />} />
			</Routes>
		</DashboardLayout>
	);
};

export default MyPage;
